import * as R from 'ramda'

import { Incident } from '@collector/sportsapi-client'
import {
  getIncidentsGroupRepresentativeIncident,
  IncidentsGroup,
  isIncidentsGroup,
} from '@desktop/views/Relation/Sports/configuration/incidentsGroup'

import {
  IncidentFilter,
  IncidentFilterSelect,
  IncidentFilterToggle,
} from './types'

export function filterIncidents(
  incidents: (Incident | IncidentsGroup)[],
  incidentFilters: IncidentFilter[],
): (Incident | IncidentsGroup)[] {
  const filterFunctions = incidentFilters.map((filter) => filter.filterFunction)
  const matchesFilterFunctions = R.allPass(filterFunctions)

  return incidents.filter((value) => {
    if (isIncidentsGroup(value)) {
      return R.any(matchesFilterFunctions, value.incidents)
    }

    return matchesFilterFunctions(value)
  })
}

export function getIncidentFiltersToggle(
  filters: IncidentFilterToggle[],
): IncidentFilter[] {
  const incidentFilters: IncidentFilter[] = []

  for (const filter of filters) {
    if (filter.value) {
      incidentFilters.push({
        name: filter.name,
        filterFunction: filter.filterFunction,
      })
    }
  }

  return incidentFilters
}

export function getIncidentFilterName(filter: IncidentFilter): string {
  return filter.name || filter.customName || ''
}

export function getIncidentFiltersSelect(
  filters: IncidentFilterSelect[],
): IncidentFilter[] {
  const incidentFilters: IncidentFilter[] = []

  for (const filter of filters) {
    const hasValue = Array.isArray(filter.value)
      ? filter.value.length > 0
      : filter.value !== undefined

    if (hasValue) {
      incidentFilters.push({
        name: filter.name,
        filterFunction: R.partialRight(filter.filterFunction, [
          // prefill last argument of filter function (currying) with current value from multiselect
          Array.isArray(filter.value)
            ? new Set(R.flatten(filter.value))
            : filter.value,
        ]),
      })
    }
  }

  return incidentFilters
}

export function sortIncidents(
  incidents: (Incident | IncidentsGroup)[],
): (Incident | IncidentsGroup)[] {
  return R.sort((a, b) => {
    const aIncident = isIncidentsGroup(a)
      ? getIncidentsGroupRepresentativeIncident(a)
      : a
    const bIncident = isIncidentsGroup(b)
      ? getIncidentsGroupRepresentativeIncident(b)
      : b

    if (aIncident.occurred_at === bIncident.occurred_at) {
      return 0
    }

    return (aIncident.occurred_at ?? 0) > (bIncident.occurred_at ?? 0) ? 1 : -1
  }, incidents)
}

export function findFilteredIncidentIndex(
  incident: Incident,
  filteredIncidents: (Incident | IncidentsGroup)[],
): number {
  const isIncidentMatching = (
    filteredIncident: Incident | IncidentsGroup,
    incident: Incident,
  ): boolean =>
    isIncidentsGroup(filteredIncident)
      ? filteredIncident.incidents.some(
          (groupIncident) => groupIncident.id === incident.id,
        )
      : filteredIncident.id === incident.id

  return R.findIndex(
    (filteredIncident) => isIncidentMatching(filteredIncident, incident),
    filteredIncidents,
  )
}

export function getHistoryItemRepresentativeId(
  item: Incident | IncidentsGroup,
): number {
  return isIncidentsGroup(item)
    ? getIncidentsGroupRepresentativeIncident(item).id
    : item.id
}

type SyncFilteredIncidentsParams = {
  filteredIncidents: (Incident | IncidentsGroup)[]
  added: (Incident | IncidentsGroup)[]
  removed: (Incident | IncidentsGroup)[]
  activeIncidentFilters: IncidentFilter[]
}

/**
 * Keeps newly sent incidents at the top while in-place updates (e.g. substitution
 * pairing) stay at their chronological position instead of jumping to the top.
 */
export function syncFilteredIncidentsOnGroupsChange({
  filteredIncidents,
  added,
  removed,
  activeIncidentFilters,
}: SyncFilteredIncidentsParams): (Incident | IncidentsGroup)[] {
  const nextFilteredIncidents = [...filteredIncidents]
  const removedByRepresentativeId = new Map(
    removed.map((item) => [getHistoryItemRepresentativeId(item), item]),
  )

  for (const addedItem of added) {
    const representativeId = getHistoryItemRepresentativeId(addedItem)
    const matchingRemoved = removedByRepresentativeId.get(representativeId)

    if (matchingRemoved) {
      removedByRepresentativeId.delete(representativeId)

      const removedIndex = nextFilteredIncidents.indexOf(matchingRemoved)

      if (removedIndex === -1) {
        continue
      }

      const [filteredReplacement] = filterIncidents(
        [addedItem],
        activeIncidentFilters,
      )

      if (filteredReplacement) {
        nextFilteredIncidents.splice(removedIndex, 1, filteredReplacement)
      } else {
        nextFilteredIncidents.splice(removedIndex, 1)
      }

      continue
    }

    const newFilteredIncidents = filterIncidents(
      [addedItem],
      activeIncidentFilters,
    )

    if (newFilteredIncidents.length) {
      nextFilteredIncidents.push(...newFilteredIncidents)
    }
  }

  for (const removedItem of removedByRepresentativeId.values()) {
    const removedIndex = nextFilteredIncidents.indexOf(removedItem)

    if (removedIndex !== -1) {
      nextFilteredIncidents.splice(removedIndex, 1)
    }
  }

  return nextFilteredIncidents
}
